// Canonical enum values used across the application.
// Sourced from the doc pack (CORRECTION_MEMO_V2.md and Phase 6a §22) — keep alignment exact.

export const WORKFLOW_STATES = Object.freeze([
  'draft',
  'submitted',
  'supervisor_review',
  'security_review',
  'portfolio_owner_review',
  'data_steward_review',
  'admin_provisioning',
  'provisioned',
  'active',
  'returned_for_rework',
  'denied',
  'withdrawn',
  'recertification_due',
  'expired',
  'revoked',
]);

export const LIFECYCLE_STAGES = Object.freeze([
  'concept',
  'planning',
  'design',
  'development',
  'integration',
  'verification',
  'production',
  'sustainment',
  'retirement',
]);

export const HEALTH_STATUSES = Object.freeze(['green', 'amber', 'red', 'unknown']);

export const GATE_CRITERION_STATES = Object.freeze([
  'complete',
  'pending',
  'blocked',
  'exception',
  'missing',
]);

export const AI_REVIEW_STATES = Object.freeze([
  'pending_human_review',
  'approved_for_export',
  'human_reviewed',
  'rejected',
  'needs_rework',
]);

export const ACCESS_REQUEST_ACTIONS = Object.freeze([
  'submit',
  'approve',
  'deny',
  'requestRework',
  'revoke',
  'recertify',
]);

export const PERSONAS = Object.freeze({
  EXECUTIVE_SPONSOR: 'executive_sponsor',
  DELIVERY_LEADER: 'delivery_leader',
  DATA_STEWARD: 'data_steward',
  PENDING_ACCESS_USER: 'pending_access',
});

export const ERROR_CODES = Object.freeze({
  BAD_REQUEST: { status: 400, code: 'BAD_REQUEST' },
  VALIDATION_ERROR: { status: 422, code: 'VALIDATION_ERROR' },
  CLOSEOUT_BLOCKED: { status: 422, code: 'CLOSEOUT_BLOCKED' },
  UNAUTHENTICATED: { status: 401, code: 'UNAUTHENTICATED' },
  ACCESS_DENIED: { status: 403, code: 'ACCESS_DENIED' },
  CAC_NOT_APPROVED: { status: 403, code: 'CAC_NOT_APPROVED' },
  NOT_FOUND: { status: 404, code: 'NOT_FOUND' },
  CONFLICT: { status: 409, code: 'CONFLICT' },
  INVALID_IDEMPOTENCY_KEY: { status: 400, code: 'INVALID_IDEMPOTENCY_KEY' },
  IDEMPOTENCY_KEY_REUSED: { status: 409, code: 'IDEMPOTENCY_KEY_REUSED' },
  IDEMPOTENCY_IN_PROGRESS: { status: 409, code: 'IDEMPOTENCY_IN_PROGRESS' },
  RESOURCE_MUTATION_IN_PROGRESS: { status: 409, code: 'RESOURCE_MUTATION_IN_PROGRESS' },
  FEEDBACK_ALREADY_CONVERTED: { status: 409, code: 'FEEDBACK_ALREADY_CONVERTED' },
  FEEDBACK_PACKAGE_ALREADY_SUBMITTED: { status: 409, code: 'FEEDBACK_PACKAGE_ALREADY_SUBMITTED' },
  FEEDBACK_ALREADY_NEEDS_INFO: { status: 409, code: 'FEEDBACK_ALREADY_NEEDS_INFO' },
  INVALID_STATUS_TRANSITION: { status: 409, code: 'INVALID_STATUS_TRANSITION' },
  REVISION_CONFLICT: { status: 409, code: 'REVISION_CONFLICT' },
  // Feedback central-aggregation protocol results. Registration matters: an
  // unregistered code falls through to HTTP 500, which sources treat as a
  // transient outage and retry forever.
  AGGREGATE_REVISION_CONFLICT: { status: 409, code: 'AGGREGATE_REVISION_CONFLICT' },
  AGGREGATE_BASELINE_PENDING: { status: 409, code: 'AGGREGATE_BASELINE_PENDING' },
  AGGREGATE_SCHEMA_UNSUPPORTED: { status: 400, code: 'AGGREGATE_SCHEMA_UNSUPPORTED' },
  AGGREGATE_SOURCE_UNKNOWN: { status: 403, code: 'AGGREGATE_SOURCE_UNKNOWN' },
  AGGREGATE_ENVELOPE_TOO_LARGE: { status: 413, code: 'AGGREGATE_ENVELOPE_TOO_LARGE' },
  FEEDBACK_PACKAGE_ALREADY_PURGED: { status: 409, code: 'FEEDBACK_PACKAGE_ALREADY_PURGED' },
  ASSET_UNAVAILABLE: { status: 404, code: 'ASSET_UNAVAILABLE' },
  FILTER_SCOPE_FORBIDDEN: { status: 403, code: 'FILTER_SCOPE_FORBIDDEN' },
  INVALID_CURSOR: { status: 400, code: 'INVALID_CURSOR' },
  PAYLOAD_TOO_LARGE: { status: 413, code: 'PAYLOAD_TOO_LARGE' },
  REPORT_SCOPE_UNAVAILABLE: { status: 410, code: 'REPORT_SCOPE_UNAVAILABLE' },
  ACTIVITY_IDENTITY_UNRESOLVED: { status: 409, code: 'ACTIVITY_IDENTITY_UNRESOLVED' },
  ACTIVITY_REPORT_SCOPE_UNREADY: { status: 409, code: 'ACTIVITY_REPORT_SCOPE_UNREADY' },
  // Typed-intake preliminary analysis (commit 4). 422 rather than 5xx on
  // purpose: the model produced nothing PRIME is willing to persist, the request
  // itself was well formed, and a blind retry is not the answer — the reporter is
  // offered the deterministic path instead. An unregistered code would fall
  // through to 500, which clients treat as a transient outage and retry forever.
  PRELIMINARY_ANALYSIS_UNAVAILABLE: { status: 422, code: 'PRELIMINARY_ANALYSIS_UNAVAILABLE' },
  // Help Q&A (commit 5). A question carrying protected information is refused
  // BEFORE retrieval or any model call, exactly as a support ticket is. 422 rather
  // than 5xx: the request was well formed, PRIME made a deliberate decision not to
  // send it anywhere, and only changing the text will get a different answer — a
  // retry never will.
  HELP_QUESTION_BLOCKED: { status: 422, code: 'HELP_QUESTION_BLOCKED' },
  // Two-phase idempotency fencing (commit-5 audit P1-1). A worker whose claim lease
  // expired and was reclaimed by an identical retry is refused before it can mutate
  // anything. 409 rather than 5xx: the request was valid, another attempt owns it now,
  // and retrying the SAME key is the correct next step — it will replay that attempt's
  // committed result.
  STALE_IDEMPOTENCY_CLAIM: { status: 409, code: 'STALE_IDEMPOTENCY_CLAIM' },
  INTERNAL_ERROR: { status: 500, code: 'INTERNAL_ERROR' },
  UPSTREAM_UNAVAILABLE: { status: 502, code: 'UPSTREAM_UNAVAILABLE' },
});
